import {
  SHBox,
  SHHtmlBlock,
  SHPopper,
  SHStack,
  SHTypography,
} from "@components/design-systems";
import { TooltipAllowSVG } from "@components/svgs";
import { IconButton, PopperPlacementType, useTheme } from "@mui/material";
import { hexToRGBA } from "@utils";
import { useEffect, useRef, useState } from "react";
import { configTooltipStyle } from "@components/buttons/information/config";
import { Link } from "@mui/material";
import { FieldTypeId } from "@models/configuration";

export interface ReadMoreButtonProps {
  margin?: string | number;
  title?: string | React.ReactNode;
  content?: string | React.ReactNode;
  placement?: PopperPlacementType;

  [key: string]: any;
  fieldType?: string;
}

export const ReadMoreButton = ({
  margin,
  title,
  content,
  placement = "top-start",
  fieldType,
  ...others
}: ReadMoreButtonProps) => {
  const { palette, shadows } = useTheme();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const tooltipConfig = configTooltipStyle[placement];

  const isTextLongField = fieldType === FieldTypeId.TextLong;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setOpen((prev) => !prev);
  };

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: any) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [wrapperRef, buttonRef, open]);

  return (
    <>
      <IconButton
        ref={buttonRef}
        onClick={handleClick}
        disableRipple
        sx={{
          padding: 0,
          margin: 0,
        }}
      >
        <Link
          ref={null}
          variant="body1"
          component="label"
          underline="none"
          sx={{
            padding: 0,
            margin: 0,
            fontWeight: 600,
            cursor: "pointer",
            color: palette.text.secondary,
            lineHeight: "1rem !important",
          }}
        >
          Read more...
        </Link>
      </IconButton>
      <SHPopper
        anchorEl={anchorEl}
        open={open}
        onClick={(e) => e.stopPropagation()}
        placement={placement}
        popperOptions={{ modifiers: [{ name: "flip", enabled: false }] }}
        sx={{ zIndex: 1400 }}
      >
        <SHBox pb={1}>
          <SHStack
            ref={wrapperRef}
            maxWidth={isTextLongField ? "550px" : "280px"}
            sx={{
              p: 2,
              minHeight: "20px",
              bgcolor: hexToRGBA(palette.common.white, 0.85),
              border: "1px solid #E3E3E3",
              backdropFilter: "blur(2px)",
              borderRadius: "3px",
              boxShadow: shadows[1],
              position: "relative",
            }}
          >
            {title && typeof title === "string" ? (
              <SHTypography variant="body1" zIndex="1">
                {title}
              </SHTypography>
            ) : (
              title
            )}
            {content && typeof content === "string" ? (
              <SHHtmlBlock
                variant="body3"
                zIndex={1}
                color={palette.text.disabled}
                content={content}
                textAlign={"left"}
                maxHeight={"250px"}
                overflow={"auto"}
              />
            ) : (
              <SHBox
                sx={{
                  fontSize: "13px",
                  fontWeight: 500,
                  color: palette.text.disabled,
                }}
              >
                {content}
              </SHBox>
            )}
            <SHBox sx={tooltipConfig?.sx}>
              <TooltipAllowSVG transform={tooltipConfig?.transform} />
            </SHBox>
          </SHStack>
        </SHBox>
      </SHPopper>
    </>
  );
};
